import React, { useState } from 'react';
import { Building2, ArrowRight, ArrowLeft, Check, Loader2, Rocket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  {
    title: 'Welcome — let’s set up your site',
    intro: 'A few quick details make this portal your own. You can change everything later in Admin → Organisation.',
    fields: [
      { key: 'name', label: 'Organisation name', placeholder: 'e.g. Springfield Rowing Club Inc.', required: true },
      { key: 'short_name', label: 'Short name', placeholder: 'e.g. Springfield Rowing' },
      { key: 'tagline', label: 'Tagline / motto', placeholder: 'e.g. Row together, win together.' },
    ],
  },
  {
    title: 'Members & money',
    intro: 'How you refer to members, and the currency you collect payments in.',
    fields: [
      { key: 'member_noun', label: 'What do you call a member?', placeholder: 'e.g. member, rower, alumnus' },
      { key: 'currency', label: 'Currency (ISO code)', placeholder: 'e.g. AUD' },
      { key: 'timezone', label: 'Timezone (IANA)', placeholder: 'e.g. Australia/Hobart' },
    ],
  },
  {
    title: 'Contact details',
    intro: 'Shown publicly and used on receipts. All optional — skip anything you don’t have yet.',
    fields: [
      { key: 'contact_email', label: 'Contact email', placeholder: 'e.g. secretary@club.org' },
      { key: 'abn', label: 'ABN / registration no.', placeholder: 'Optional' },
      { key: 'website', label: 'Website', placeholder: 'https://…' },
    ],
  },
];

export default function SetupWizard() {
  const { org, isAdmin, updateOrg, loaded } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only show to a signed-in admin, once org config has loaded and setup isn't complete.
  if (!loaded || !isAdmin || !org || org.setup_complete || dismissed) return null;

  // Seed the form with current org values on first render.
  const val = (k) => (form[k] !== undefined ? form[k] : (org[k] || ''));
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const canNext = step > 0 || (val('name') || '').trim().length > 0;

  const finish = async () => {
    setBusy(true);
    try {
      const payload = {};
      STEPS.forEach((s) => s.fields.forEach((f) => { payload[f.key] = val(f.key); }));
      await updateOrg({ ...payload, setup_complete: true });
      setDismissed(true);
    } catch (e) {
      /* keep wizard open on failure */
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} data-testid="setup-wizard">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-6 py-4 flex items-center gap-2" style={{ background: 'linear-gradient(180deg, var(--maroon), var(--maroon-dark))' }}>
          <Building2 size={20} style={{ color: 'var(--cream)' }} />
          <span className="font-title text-lg" style={{ color: 'var(--cream)' }}>First-run setup</span>
          <span className="ml-auto text-xs font-title" style={{ color: 'rgba(247,241,225,0.8)' }}>Step {step + 1} of {STEPS.length}</span>
        </div>

        <div className="px-6 py-5">
          <h2 className="font-title text-xl mb-1" style={{ color: 'var(--maroon)' }}>{current.title}</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>{current.intro}</p>

          <div className="space-y-3">
            {current.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>
                  {f.label}{f.required && <span style={{ color: 'var(--maroon)' }}> *</span>}
                </label>
                <input
                  value={val(f.key)}
                  onChange={(e) => set(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  data-testid={`wizard-${f.key}`}
                  className="w-full px-3 py-2 rounded border text-sm"
                  style={{ borderColor: 'rgba(0,0,0,0.2)' }}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6">
            <button
              onClick={() => (step === 0 ? setDismissed(true) : setStep((s) => s - 1))}
              data-testid="wizard-back"
              className="inline-flex items-center gap-1 text-sm font-title px-3 py-2 rounded"
              style={{ color: 'var(--navy)' }}>
              {step === 0 ? 'Skip for now' : (<><ArrowLeft size={15} /> Back</>)}
            </button>

            {isLast ? (
              <button onClick={finish} disabled={busy} data-testid="wizard-finish"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded font-title"
                style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.6 : 1 }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />} Finish setup
              </button>
            ) : (
              <button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="wizard-next"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded font-title"
                style={{ background: 'var(--navy)', color: 'var(--cream)', opacity: canNext ? 1 : 0.5 }}>
                Next <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
